import type { Discount, FilterType, FilterUiValues, Maybe } from '@/types'
import type { ToastState } from '@/types/ui'
import type { MakeMaybe, MakeRequired } from '@/types/utilities'
import type { CustomerOrderOneClickFormType } from '@/types/customer/orders'
import type { CustomerLensTableItem } from '@/types/customer/products'
import { PostElementType, PostElementSection } from '@/types'

export * from './blocks'
export * from './catalogs'
export * from './headers'
export * from './navigation'
export * from './settings'
export * from './meta'
export * from './auth'
export * from './marketing'

export const CUSTOMER_PRODUCT_PATH = import.meta.env.VITE_PRODUCTS_URL ?? '/'
export const CUSTOMER_CATALOG_PATH = import.meta.env.VITE_CATALOGS_URL ?? '/'
export const CUSTOMER_BLOG_PATH = import.meta.env.VITE_BLOG_URL ?? '/'

export const SEARCH_MIN_NUMBER = 3

export const DEFAULT_TOAST_STATE: ToastState = {
  visibility: false,
  text: null,
  type: 'error',
  timeout: 3000,
}

export const FILTER_UI_DEFAULT_VALUES: Record<FilterType, FilterUiValues> = {
  multiselect: false,
  range: [0, 0],
  string: '',
  boolean: false,
}

export const DISCOUNT_NULL: MakeRequired<
  MakeMaybe<Partial<Discount>, 'id'>,
  'id'
> = {
  id: null,
  amount: 0,
  name: 'нет',
}

export const SORTABLE_IMAGE_FALSE_ID = 1000000

export const DEFAULT_IMAGE_SRC =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII='

export const MONTHS = [
  { name: '', number: null },
  { name: 'Январь', number: 1 },
  { name: 'Февраль', number: 2 },
  { name: 'Март', number: 3 },
  { name: 'Апрель', number: 4 },
  { name: 'Май', number: 5 },
  { name: 'Июнь', number: 6 },
  { name: 'Июль', number: 7 },
  { name: 'Август', number: 8 },
  { name: 'Сентябрь', number: 9 },
  { name: 'Октябрь', number: 10 },
  { name: 'Ноябрь', number: 11 },
  { name: 'Декабрь', number: 12 },
]

export const DEFAULT_ORDER_ONE_CLICK_FORM: CustomerOrderOneClickFormType = {
  items: [],
  customer_info: {
    name: null,
    phone: null,
  },
}

export const POST_ELEMENTS_SECTION: {
  alias: PostElementSection
  name: string
}[] = [
  {
    alias: PostElementSection.Media,
    name: 'Медиа',
  },
  {
    alias: PostElementSection.Text,
    name: 'Текст',
  },
  {
    alias: PostElementSection.Functional,
    name: 'Функционал',
  },
]

export const POST_ELEMENTS_TYPES: {
  alias: PostElementType
  name: string
  section: PostElementSection
}[] = [
  {
    alias: PostElementType.PhotoWide,
    name: 'Широкое фото',
    section: POST_ELEMENTS_SECTION[0].alias,
  },
  {
    alias: PostElementType.Photo2,
    name: '2 фото',
    section: POST_ELEMENTS_SECTION[0].alias,
  },
  {
    alias: PostElementType.Photo3,
    name: '3 фото',
    section: POST_ELEMENTS_SECTION[0].alias,
  },
  {
    alias: PostElementType.PhotoGallery,
    name: 'Большое фото с галереей под ним',
    section: POST_ELEMENTS_SECTION[0].alias,
  },
  {
    alias: PostElementType.Video,
    name: 'Видео',
    section: POST_ELEMENTS_SECTION[0].alias,
  },
  {
    alias: PostElementType.Text,
    name: 'Текст',
    section: POST_ELEMENTS_SECTION[1].alias,
  },
  {
    alias: PostElementType.Quote,
    name: 'Цитата',
    section: POST_ELEMENTS_SECTION[1].alias,
  },
  {
    alias: PostElementType.ProductCarousel,
    name: 'Товарный фид',
    section: POST_ELEMENTS_SECTION[2].alias,
  },
  {
    alias: PostElementType.Button,
    name: 'Кнопка',
    section: POST_ELEMENTS_SECTION[2].alias,
  },
  {
    alias: PostElementType.FAQ,
    name: 'FAQ',
    section: POST_ELEMENTS_SECTION[2].alias,
  },
]

export const BANNER_TEMPLATES_BY_COUNT: Record<number, string[][]> = {
  1: [
    ['banner_1_large', 'banner_1_large_2x'],
    ['banner_1_medium', 'banner_1_medium_2x'],
    ['banner_1_medium', 'banner_1_medium_2x'],
  ],
  2: [
    ['banner_2_large', 'banner_2_large_2x'],
    ['banner_2_medium', 'banner_2_medium_2x'],
    ['banner_2_medium', 'banner_2_medium_2x'],
  ],
  3: [
    ['banner_3_medium', 'banner_3_medium_2x'],
    ['banner_3_medium', 'banner_3_medium_2x'],
    ['banner_3_medium', 'banner_3_medium_2x'],
  ],
  4: [
    ['banner_3_medium', 'banner_3_medium_2x'],
    ['banner_3_medium', 'banner_3_medium_2x'],
    ['banner_3_medium', 'banner_3_medium_2x'],
  ],
}

export const BRAND_TEMPLATES: string[][] = [
  ['brand_large', 'brand_large_2x'],
  ['brand_medium', 'brand_medium_2x'],
  ['brand_medium', 'brand_medium_2x'],
]

export const PRESET_TEMPLATES: string[][] = [
  ['preset', 'preset_2x'],
  ['preset', 'preset_2x'],
  ['preset', 'preset_2x'],
]

export const PRODUCT_IMAGE_MORE_TEMPLATES: string[][] = [
  ['product_more', 'product_more_2x'],
  ['product_more', 'product_more_2x'],
  ['product_more', 'product_more_2x'],
]

export const PRODUCT_IMAGE_TEMPLATES: string[][] = [
  ['product_large', 'product_large_2x'],
  ['product_large', 'product_large_2x'],
  ['product_medium', 'product_medium_2x'],
]
// TODO: надо бы, чтобы бэк присылал поле prefix?
export const FILTER_PREFIX: Record<string, Maybe<string>> = {
  ['Материал']: null,
  ['Бренд']: null,
  'Категория товара': null,
  'Размер скидки': 'Скидка',
  'Цвет материала': 'Цвет материала',
  ['Пол']: null,
  'Покрытие материала': 'Покрытие',
  'Цвет линз': 'Цвет линз',
  'Свойства линз': 'Линзы',
  'Прозрачность материала': 'Материал',
  'Материал линз': 'Линзы',
  'Форма оправы': 'Форма',
  'Ширина линзы': 'Ширина линзы',
}
/**
 * Lenses
 * */
export const LENS_TYPES = ['Узкие', 'Средние', 'Широкие']
export const LENS_SIZES = [42, 44, 45, 46, 47, 48, 49, 50, 51, 52, '53+']

export const MENU_LENSES = [
  { name: 'Nikon', url: 'nikon' },
  { name: 'Synchrony', url: 'synchrony' },
  { name: 'Zeiss', url: 'zeiss' },
]

export const TABLE_LENSES: Record<string, string> = {
  option_39: 'line',
  option_41: 'design',
  option_40: 'coefficient',
  option_44: 'layer',
  option_42: 'purpose',
  option_43: 'color',
  option_45: 'layerDescription',
}

export const SIMPLE_TABLE_LENSES_HEADERS: Partial<
  Record<keyof CustomerLensTableItem, string>
> = {
  line: 'Линейка',
  design: 'Дизайн линзы',
  coefficient: 'Коэффициент утончения',
  layer: 'Покрытие',
  price: 'Цена',
}

export const SIMPLE_TABLE_LENSES_WITH_COLOR_HEADERS: Partial<
  Record<keyof CustomerLensTableItem, string>
> = {
  line: 'Линейка',
  design: 'Дизайн линзы',
  coefficient: 'Коэффициент утончения',
  layer: 'Покрытие',
  color: 'Цвет',
  price: 'Цена',
}

export const SIMPLE_TABLE_LENSES_HEADERS_MOBILE: Partial<
  Record<keyof CustomerLensTableItem | string, string>
> = {
  params: 'Параметры линзы',
  price: 'Цена',
}

export const LENSES_BRAND_PURPOSES: Record<string, string[]> = {
  nikon: ['для повседневного использования', 'фотохромные'],
  synchrony: [
    'для повседневного использования',
    'фотохромные',
    'солнцезащитные',
    'для чтения',
  ],
  zeiss: [
    'для повседневного использования',
    'защита от синего цвета',
    'для вождения',
    'фотохромные',
    'минеральные',
  ],
}

export const LENSES_DESIGN_DESCRIPTION: Record<string, string> = {
  ['сферические']:
    'Сферические линзы\xa0\u2014 это полусферы с\xa0одинаковым радиусом кривизны по\xa0всей площади. Оптическая сила сосредоточена в\xa0центре линзы, таким образом именно в\xa0этой части обеспечена наилучшая четкость зрения.',

  ['асферические']:
    'Асферические линзы имеют более сложный дизайн и\xa0напоминают форму эллипса. В\xa0очках с\xa0асферическими линзами четко виден и\xa0центр, и\xa0периферия. За\xa0счет этого увеличивается острота зрения и\xa0контрастность изображения, а\xa0процент визуальных искажений, наоборот, уменьшается.',

  'разгрузка аккомодации':
    'Аккомодация\xa0\u2014 это способность глаза фокусироваться на\xa0предметах вблизи и\xa0вдали. Линзы с\xa0разгрузкой аккомодации позволяют бороться со\xa0зрительным утомлением за\xa0счет постепенно снижающейся оптической силы. Подходят школьникам, студентам, офисным работникам и\xa0другим, чья деятельность связана с\xa0необходимостью долго фокусировать взгляд на\xa0близких расстояниях. Можно использовать для постоянного ношения.',

  'free form':
    'Технология FreeForm\u2122\xa0\u2014 это расчет и\xa0изготовление поверхности линзы с\xa0переменной кривизной, с\xa0точностью до\xa0сорока тысяч точек на\xa0поверхность. Данная технология позволяет изготавливать полностью индивидуальные линзы для ваших очков с\xa0высокой точностью. При этом учитываются индивидуальные особенности зрительного аппарата пользователя. Используется при производстве офисных и\xa0прогрессивных линз. Не\xa0требуют адаптации.',

  ['офисные']:
    'Современные улучшенные линзы для чтения. Обеспечивают четкое и\xa0расслабленное зрение в\xa0диапазоне от\xa0близкого расстояния для чтения до\xa0средних расстояний. Легкая адаптация и\xa0зрительный комфорт при длительной нагрузке (например, при работе за\xa0компьютером и\xa0чтении).',

  'двойная асферика':
    'Двойной асферический дизайн делает линзу более плоской с\xa0обеих сторон, благодаря чему она становится еще тоньше и\xa0легче. Обеспечивают четкое и\xa0комфортное зрение без искажений даже по\xa0краям линз. Гарантируют эстетически привлекательный внешний вид.',
}
